import { useId } from 'react'
import type { MascotAppearance } from './mascot-appearance'
import styles from './DonorMascot.module.css'

export function MascotFace({
  appearance: a,
  cat,
  eyeFill,
  jaguar = false,
}: {
  appearance: MascotAppearance
  cat: boolean
  eyeFill: string
  jaguar?: boolean
}) {
  const eyeClip = `eyes-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`
  const droop = a.delighted ? 0 : Math.min(0.72, a.fatigue * 0.58 + a.sickness * 0.27)
  const lidY = -8 + droop * 24
  const lidCurve = -28 + droop * 50
  const worry = a.concern
  const smile = 9 - worry * 19
  return (
    <g className={styles.face}>
      <defs>
        <clipPath id={eyeClip}>
          <path d={`M-16 ${lidY}Q0 ${lidCurve} 16 ${lidY}V24H-16Z`} />
        </clipPath>
      </defs>
      <ellipse
        cx="70"
        cy="127"
        rx="12"
        ry="6.5"
        fill="#e99c95"
        opacity={0.28 + a.sickness * 0.42}
      />
      <ellipse
        cx="170"
        cy="127"
        rx="12"
        ry="6.5"
        fill="#e99c95"
        opacity={0.28 + a.sickness * 0.42}
      />
      <g
        fill="none"
        stroke="#664d45"
        strokeWidth="2.8"
        strokeLinecap="round"
        opacity={0.42 + worry * 0.35}
      >
        <path d={`M77 ${85 + worry * 2}q9 ${-5 - worry * 6} 18 ${-worry * 6}`} />
        <path d={`M145 ${85 - worry * 6}q9 ${-5 - worry * 2} 18 ${worry * 8}`} />
      </g>
      {a.sleeping || a.delighted || a.yawning ? (
        <g fill="none" stroke="#493a37" strokeWidth="3.5" strokeLinecap="round">
          <path
            d={
              a.sleeping || a.yawning
                ? 'M77 108q12 13 24 0m38 0q12 13 24 0'
                : 'M77 109q12-17 24 0m38 0q12-17 24 0'
            }
          />
          {a.sleeping && (
            <path d="m80 112-3 3m23-3 3 3m36-3-3 3m23-3 3 3" strokeWidth="2" />
          )}
        </g>
      ) : (
        <>
          {[89, 151].map((x) => (
            <g key={x} transform={`translate(${x} 108)`}>
              <g className={styles.blink}>
                <g clipPath={`url(#${eyeClip})`}>
                  <ellipse rx="16" ry="20" cy="1" fill="#745343" opacity=".13" />
                  <ellipse rx="14.5" ry="18" fill="#fffaf0" />
                  <g className={styles.gaze}>
                    <ellipse rx="11.7" ry="15.5" cy="1" fill={eyeFill} />
                    <ellipse
                      rx={jaguar ? 8.5 : cat ? 6.6 : 8}
                      ry="12"
                      cy="-1"
                      fill="#292c32"
                    />
                    <ellipse cx="-4" cy="-7" rx="4.7" ry="5.1" fill="#fffef8" />
                    <circle cx="5" cy="6" r="2.1" fill="#fff9e2" opacity=".9" />
                    <path
                      d="M-5 12q5 3 10-1"
                      fill="none"
                      stroke={cat ? '#c5e9ba' : '#edbd80'}
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      opacity=".65"
                    />
                  </g>
                  <ellipse
                    cy="13"
                    rx="10"
                    ry="2.3"
                    fill="#c8e2e4"
                    opacity={Math.max(a.sadness, a.hunger) * 0.35}
                  />
                </g>
                <path
                  d={`M-13 ${lidY}Q0 ${lidCurve} 13 ${lidY}`}
                  fill="none"
                  stroke="#493a37"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
              </g>
              <path
                d="M-9 17q9 4 18 0"
                fill="none"
                stroke="#b88c88"
                strokeWidth="3"
                strokeLinecap="round"
                opacity={a.sickness * 0.7}
              />
            </g>
          ))}
        </>
      )}
      <path
        d={
          jaguar
            ? 'M87 134q0-17 21-14 12 5 24 0 21-3 21 14 0 20-33 19t-33-19'
            : 'M96 132q1-13 14-11 10 4 20 0 13-2 14 11 0 15-24 15t-24-15'
        }
        fill="#fff3df"
        opacity=".95"
      />
      <path
        d={
          jaguar
            ? 'M108 124q12-7 24 0 2 6-12 12-14-6-12-12'
            : 'M111 125q9-5 18 0 1 5-9 10-10-5-9-10'
        }
        fill={jaguar ? '#45302c' : cat ? '#ae7775' : '#59423e'}
      />
      {jaguar && (
        <g fill="#594139" opacity=".7">
          {[96, 103, 137, 144].map((x, i) => (
            <circle key={x} cx={x} cy={132 + (i % 2) * 6} r="1.5" />
          ))}
        </g>
      )}
      <path
        d="M115 125q4-2 8-1"
        fill="none"
        stroke={cat ? '#f1c7bd' : '#af8c79'}
        strokeWidth="2"
        strokeLinecap="round"
      />
      {a.yawning ? (
        <g className={styles.yawnMouth}>
          <ellipse cx="120" cy="145" rx="10" ry="13" fill="#70423e" />
          <ellipse cx="120" cy="152" rx="6" ry="4" fill="#eea3a1" />
        </g>
      ) : a.delighted ? (
        <g>
          <path d="M108 138q12 4 24 0-1 16-12 16t-12-16" fill="#70423e" />
          <path d="M113 147q7-6 14 0v3q-7 8-14 0Z" fill="#eea3a1" />
          <path d="M120 146v5" stroke="#d17e89" strokeWidth="1.2" strokeLinecap="round" />
        </g>
      ) : a.sleeping ? (
        <path
          d="M112 140q8 6 16 0"
          fill="none"
          stroke="#79544b"
          strokeWidth="2"
          strokeLinecap="round"
        />
      ) : (
        <g fill="none" stroke="#79544b" strokeWidth="2.1" strokeLinecap="round">
          <path d="M120 135v4" />
          <path d={`M107 139q6 ${smile} 13 0 7 ${smile} 13 0`} />
        </g>
      )}
      {cat && (
        <g
          fill="none"
          stroke="#785e56"
          strokeWidth="1.4"
          strokeLinecap="round"
          opacity=".6"
        >
          <path d="m72 133-25-4m26 10-27 3m122-9 25-4m-26 12 27 3" />
          <circle cx="99" cy="132" r=".7" />
          <circle cx="141" cy="132" r=".7" />
        </g>
      )}
      {!cat && (
        <g fill="#ae8b76" opacity=".55">
          <circle cx="103" cy="131" r="1" />
          <circle cx="106" cy="136" r="1" />
          <circle cx="137" cy="131" r="1" />
          <circle cx="134" cy="136" r="1" />
        </g>
      )}
      {a.sickness > 0 && (
        <g opacity={a.sickness}>
          <path
            d="M175 82q-7 10-1 13 8 1 1-13"
            fill="#b3dadd"
            stroke="#8fbdc8"
            strokeWidth=".7"
          />
          <path
            d="m174 87-1 4"
            stroke="#f1ffff"
            strokeWidth="1.3"
            strokeLinecap="round"
          />
          <path
            d="m64 126 2 3m4-4 2 3m95-2 2 3m4-4 2 3"
            stroke="#cc8180"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </g>
      )}
    </g>
  )
}
